<template>
  <v-card class="pt-4 pr-4 pl-4" flat max-width="500">
    <v-form ref="form" v-model="valid">
      <div v-for="(item, index) in witnessList" :key="index">
        <v-card-text>
          {{ $t("report.witness.itemTitle", { number: index + 1 }) }}

          <v-btn elevation="0" small class="mx-2" color="error" @click="removeWittness(index)" data-test-remove-witness>
            {{ $t("report.witness.remove") }}
          </v-btn>
        </v-card-text>
        <v-autocomplete
          :rules="roleRules"
          outlined
          :label="$t('report.witness.role') + ' *'"
          v-model="item.role"
          :items="witnessRolesItems"
          data-test-witness-role
        >
          <template #item="{ item }"> {{ $t(`report.witness.roles.${item}`) }} </template>
          <template #selection="{ item }"> {{ $t(`report.witness.roles.${item}`) }} </template>
        </v-autocomplete>
        <v-text-field
          outlined
          required
          :rules="nameRule"
          :label="$t('report.witness.firstName') + ' *'"
          v-model="item.firstName"
          data-test-witness-first-name
        />
        <v-text-field
          outlined
          required
          :rules="nameRule"
          :label="$t('report.witness.lastName') + ' *'"
          v-model="item.lastName"
          data-test-witness-last-name
        />
        <v-text-field
          outlined
          :rules="phoneNumberRules"
          :label="$t('report.witness.phone')"
          v-model="item.contact.phone"
          data-test-witness-phone
        />
        <v-text-field
          outlined
          :rules="emailRules"
          :label="$t('report.witness.email')"
          v-model="item.contact.email"
          data-test-witness-mail
        />

        <v-checkbox
          v-if="isInsuranceDisplayed"
          v-model="hasInsurance"
          :label="$t(`report.witness.insurance`)"
        ></v-checkbox>
        <insurance-form v-if="hasInsurance" v-model="item.insurances[0]" />

        <v-text-field
          v-if="numberplate"
          outlined
          :label="$t('report.witness.numberplate')"
          :rules="numberplateRules"
          v-model="item.numberplate"
          data-test-witness-numberplate
        />

        <v-checkbox v-model="item.isInjured" :label="$t(`report.witness.isInjured`)"></v-checkbox>
        <v-textarea
          outlined
          required
          :label="$t(`report.witness.isInjuredDetail`) + ' *'"
          v-model="item.note"
          :rules="noteRule"
          v-if="item.isInjured"
        ></v-textarea>
        <v-card-text>
          {{ $t(`report.witness.appendix`) }}
        </v-card-text>
        <report-image-upload
          :asset="assetFront"
          :example="exampleFront"
          :instruction="instruction"
          :type="typeFront"
          :imageIds="item.imageIds"
          @onImageUpload="(image) => onImageUpload(image, index)"
          @onImageDeleted="(image) => onImageDeleted(image, index)"
          data-test-image
        />
        <v-divider class="mt-2 mb-4"></v-divider>
      </div>

      <v-btn elevation="0" color="success" v-if="!witnessList.length" @click="addWittness" data-test-add-witness>
        {{ $t("report.witness.add") }}
      </v-btn>
      <span v-else>
        <v-btn elevation="0" color="success" @click="addWittness" data-test-add-another-witness>
          {{ $t("report.witness.addFurther") }}</v-btn
        >
      </span>
    </v-form>
  </v-card>
</template>

<script lang="ts">
import LayoutReportStep from "@/layouts/LayoutReportStep.vue";
import { AssetEnum } from "@/lib/AssetRepository";
import { ReportRoleEnum } from "@/lib/enum/ReportRole.enum";
import { getDefaultPartnerColor } from "@/lib/getDefaultPartnerColor";
import { IReportInstructionData } from "@/lib/interfaces/Report/IReportInstructionData";
import { emailRuleOptional, phoneRuleOptional } from "@/lib/rules/contactRule";
import { numberplateRule } from "@/lib/rules/numberplateRule";
import { requiredRule } from "@/lib/rules/requiredRule";
import { deepCopy } from "@/lib/utility/deep-copy";
import { IImageUploaded } from "@/models/Image/IImageUploaded";
import { ReportImageType } from "@/models/Report/ReportImageType";
import { MrfiktivCreateReportWitnessDtoGen } from "@/services/mrfiktiv/v1/data-contracts";
import { ReportModule } from "@/store/modules/report.store";
import ReportImageUpload from "@/views/report/ReportImageUpload.vue";
import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import NumberplateForm from "../utility/NumberplateForm.vue";
import InsuranceForm from "./InsuranceForm.vue";

@Component({
  components: {
    LayoutReportStep,
    ReportImageUpload,
    NumberplateForm,
    InsuranceForm
  }
})
export default class WitnessesForm extends Vue {
  @Prop({ default: false })
  numberplate!: boolean;

  valid = false;
  hasInsurance = false;

  /**
   * Feature flag
   */
  isInsuranceDisplayed = false;

  @Watch("valid", { deep: true })
  emitValid() {
    this.$emit("valid", this.valid);
  }

  @Watch("hasInsurance")
  toggleInsurance() {
    if (!this.hasInsurance) {
      this.witness.insurances?.splice(0);
    } else if (this.witness.insurances) {
      this.witness.insurances.push({ name: "", number: "" });
    }
  }

  get isDone() {
    return this.valid;
  }

  assetFront = AssetEnum.driversLicenseExampleFrontOutlined;
  exampleFront = AssetEnum.driversLicenseExampleFront;

  instruction: IReportInstructionData = {
    title: "",
    text: "",
    pictures: [AssetEnum.registration],
    example: AssetEnum.registration,
    display: false
  };

  witness: MrfiktivCreateReportWitnessDtoGen = {
    firstName: "",
    lastName: "",
    contact: {
      email: "",
      phone: ""
    },
    imageIds: [],
    role: "" as ReportRoleEnum,
    isInjured: false,
    note: "",
    numberplate: "",
    insurances: []
  };

  ReportRoleEnum = ReportRoleEnum;

  get witnessRolesItems() {
    return Object.values(ReportRoleEnum);
  }

  get typeFront() {
    return ReportImageType.driverslicensefront;
  }

  get witnessList() {
    return ReportModule.wittnessList;
  }

  set witnessList(list: MrfiktivCreateReportWitnessDtoGen[]) {
    ReportModule.setWitnessList(list);
  }

  get numberplateRules() {
    const rules = [];

    rules.push(numberplateRule());

    return rules;
  }

  get emailRules() {
    const rules = [];

    rules.push(emailRuleOptional());

    return rules;
  }

  get nameRule() {
    const rules = [];
    rules.push(requiredRule());

    return rules;
  }

  get noteRule() {
    const rules = [];
    rules.push(requiredRule());

    return rules;
  }

  get phoneNumberRules() {
    const rules = [];

    rules.push(phoneRuleOptional());

    return rules;
  }

  get roleRules() {
    const rules = [];

    rules.push(requiredRule());

    return rules;
  }

  get color() {
    return getDefaultPartnerColor();
  }

  addWittness() {
    this.witnessList.push(deepCopy(this.witness));
  }

  removeWittness(index: number) {
    this.witnessList.splice(index, 1);
  }

  onImageUpload(image: IImageUploaded, index: number) {
    this.witnessList[index].imageIds.push(image.uploadId);
  }

  onImageDeleted(images: IImageUploaded[], index: number) {
    const removed = images.map((i) => i.uploadId);
    const filtered = this.witnessList[index].imageIds.filter((i: string) => {
      return !removed.includes(i);
    });

    this.witnessList[index].imageIds.splice(0);
    this.witnessList[index].imageIds.push(...filtered);
  }
}
</script>
